import { useCallback } from 'react';
import type { EntityTypes } from '../../../context/DependencyGraphProvider/types';
import { useBuilderInterfaceAppStore, useBuilderInterfaceStore } from './hooks';
import { getActiveInterfacePage } from './state';

export const useGetInterfaceStoreState = () => {
  return {
    getInterfaceStoreState: useBuilderInterfaceStore().getState,
  };
};

export const useGetAppInterfaceStoreState = () => {
  return {
    getAppInterfaceStoreState: useBuilderInterfaceAppStore().getState,
  };
};

export const useGetGlobalPageFolders = () => {
  const { getAppInterfaceStoreState } = useGetAppInterfaceStoreState();
  const getGlobalPageFolders = useCallback(() => {
    const activeInterfacePage = getActiveInterfacePage(getAppInterfaceStoreState());
    return activeInterfacePage?.properties.folders;
  }, [getAppInterfaceStoreState]);

  return {
    getGlobalPageFolders,
  };
};

export const useGetPageFolders = () => {
  const { getAppInterfaceStoreState } = useGetAppInterfaceStoreState();
  const getPageFolders = useCallback(() => {
    const activeInterfacePage = getActiveInterfacePage(getAppInterfaceStoreState());
    return activeInterfacePage?.properties.blocks;
  }, [getAppInterfaceStoreState]);

  return {
    getPageFolders,
  };
};

export const useGetAppVariables = () => {
  const { getAppInterfaceStoreState } = useGetAppInterfaceStoreState();
  const getAppVariables = useCallback(() => {
    const activeInterfacePage = getActiveInterfacePage(getAppInterfaceStoreState());
    return activeInterfacePage?.properties.pageVariables;
  }, [getAppInterfaceStoreState]);

  return {
    getAppVariables,
  };
};

export const useGetAppFunctions = () => {
  const { getAppInterfaceStoreState } = useGetAppInterfaceStoreState();
  const getAppFunctions = useCallback(() => {
    const activeInterfacePage = getActiveInterfacePage(getAppInterfaceStoreState());
    return activeInterfacePage?.properties.pageFunctions;
  }, [getAppInterfaceStoreState]);

  return {
    getAppFunctions,
  };
};

export const useGetIsGlobalPageEntity = () => {
  const { getAppVariables } = useGetAppVariables();
  const { getAppFunctions } = useGetAppFunctions();
  const { getGlobalPageFolders } = useGetGlobalPageFolders();

  const getIsGlobalPageEntity = useCallback(
    ({ entityId, type }: { entityId: string; type: EntityTypes | 'folders' }) => {
      switch (type) {
        case 'pageVariables': {
          return !!getAppVariables()?.[entityId];
        }
        case 'pageFunctions': {
          return !!getAppFunctions()?.[entityId];
        }
        case 'folders': {
          return !!getGlobalPageFolders()?.[entityId];
        }
        default:
          throw new Error('Unknown entity type');
      }
    },
    [getAppVariables, getAppFunctions, getGlobalPageFolders],
  );

  return {
    getIsGlobalPageEntity,
  };
};

export const useGetBuilderInterfaceStoreByEntity = () => {
  const { getInterfaceStoreState } = useGetInterfaceStoreState();
  const { getAppInterfaceStoreState } = useGetAppInterfaceStoreState();
  const { getIsGlobalPageEntity } = useGetIsGlobalPageEntity();

  const getBuilderInterfaceStoreByEntity = useCallback(
    ({ entityId, type }: { entityId: string; type: EntityTypes | 'folders' }) => {
      const isGlobalPageEntity = getIsGlobalPageEntity({ entityId, type });
      return isGlobalPageEntity ? getAppInterfaceStoreState() : getInterfaceStoreState();
    },
    [getInterfaceStoreState, getAppInterfaceStoreState, getIsGlobalPageEntity],
  );

  return {
    getBuilderInterfaceStoreByEntity,
  };
};

const useGetBuilderInterfaceStoreActionsByEntity = () => {
  const { getBuilderInterfaceStoreByEntity } = useGetBuilderInterfaceStoreByEntity();

  const getBuilderInterfaceStoreActionsByEntity = useCallback(
    ({ entityId, type }: { entityId: string; type: EntityTypes | 'folders' }) => {
      const builderInterfaceStore = getBuilderInterfaceStoreByEntity({ entityId, type });
      return builderInterfaceStore.actions;
    },
    [getBuilderInterfaceStoreByEntity],
  );

  return {
    getBuilderInterfaceStoreActionsByEntity,
  };
};

export const useGetPageVariableInterfaceStoreActions = () => {
  const { getBuilderInterfaceStoreActionsByEntity } = useGetBuilderInterfaceStoreActionsByEntity();
  const { getInterfaceStoreByTarget } = useGetInterfaceStoreByTarget();
  const getPageVariableInterfaceStoreActions = useCallback(
    (entityId: string) => {
      return getBuilderInterfaceStoreActionsByEntity({ entityId, type: 'pageVariables' })
        .pageVariableActions;
    },
    [getBuilderInterfaceStoreActionsByEntity],
  );

  const getPageVariableInterfaceStoreActionsByTarget = useCallback(
    (target: 'app' | 'page') => {
      return getInterfaceStoreByTarget(target).actions.pageVariableActions;
    },
    [getInterfaceStoreByTarget],
  );

  return {
    getPageVariableInterfaceStoreActions,
    getPageVariableInterfaceStoreActionsByTarget,
  };
};

export const useGetJavascriptInterfaceStoreActions = () => {
  const { getInterfaceStoreByTarget } = useGetInterfaceStoreByTarget();

  const getJavascriptInterfaceStoreActionsByTarget = useCallback(
    (target: 'app' | 'page') => {
      return getInterfaceStoreByTarget(target).actions.javascript;
    },
    [getInterfaceStoreByTarget],
  );

  return {
    getJavascriptInterfaceStoreActionsByTarget,
  };
};

export const useGetFolderInterfaceStoreActions = () => {
  const { getBuilderInterfaceStoreByEntity } = useGetBuilderInterfaceStoreByEntity();
  const { getInterfaceStoreByTarget } = useGetInterfaceStoreByTarget();

  const getFolderInterfaceStoreActions = useCallback(
    (entityId: string) => {
      return getBuilderInterfaceStoreByEntity({ entityId, type: 'folders' }).actions
        .interfacePageFolderActions;
    },
    [getBuilderInterfaceStoreByEntity],
  );

  const getFolderInterfaceStoreActionsByTarget = useCallback(
    (target: 'app' | 'page') => {
      return getInterfaceStoreByTarget(target).actions.interfacePageFolderActions;
    },
    [getInterfaceStoreByTarget],
  );

  return {
    getFolderInterfaceStoreActions,
    getFolderInterfaceStoreActionsByTarget,
  };
};

export const useGetPageFunctionInterfaceStoreActions = () => {
  const { getBuilderInterfaceStoreActionsByEntity } = useGetBuilderInterfaceStoreActionsByEntity();
  const { getInterfaceStoreByTarget } = useGetInterfaceStoreByTarget();
  const getPageFunctionInterfaceStoreActions = useCallback(
    (entityId: string) => {
      return getBuilderInterfaceStoreActionsByEntity({ entityId, type: 'pageFunctions' })
        .pageFunctionActions;
    },
    [getBuilderInterfaceStoreActionsByEntity],
  );

  const getPageFunctionInterfaceStoreActionsByTarget = useCallback(
    (target: 'app' | 'page') => {
      return getInterfaceStoreByTarget(target).actions.pageFunctionActions;
    },
    [getInterfaceStoreByTarget],
  );

  return {
    getPageFunctionInterfaceStoreActions,
    getPageFunctionInterfaceStoreActionsByTarget,
  };
};

export const useGetInterfaceStoreByTarget = () => {
  const { getAppInterfaceStoreState } = useGetAppInterfaceStoreState();
  const { getInterfaceStoreState } = useGetInterfaceStoreState();

  const getInterfaceStoreByTarget = useCallback(
    (target: 'app' | 'page') => {
      return target === 'app' ? getAppInterfaceStoreState() : getInterfaceStoreState();
    },
    [getAppInterfaceStoreState, getInterfaceStoreState],
  );

  return {
    getInterfaceStoreByTarget,
  };
};

export const useGetFoldersInterfaceStoreActions = () => {
  const { getBuilderInterfaceStoreByEntity } = useGetBuilderInterfaceStoreByEntity();
  const { getInterfaceStoreByTarget } = useGetInterfaceStoreByTarget();
  const getFoldersInterfaceStoreActions = useCallback(
    (entityId: string) => {
      return getBuilderInterfaceStoreByEntity({ entityId, type: 'folders' }).actions
        .interfacePageFolderActions;
    },
    [getBuilderInterfaceStoreByEntity],
  );

  const getFoldersInterfaceStoreActionsByTarget = useCallback(
    (target: 'app' | 'page') => {
      return getInterfaceStoreByTarget(target).actions.interfacePageFolderActions;
    },
    [getInterfaceStoreByTarget],
  );

  return {
    getFoldersInterfaceStoreActions,
    getFoldersInterfaceStoreActionsByTarget,
  };
};
